import type { FC } from "react";
import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { CampaignPicker } from "app/components/campaigns/queryBar-report/CampaignPicker";
import type { ILocationObject } from "app/components/campaigns/queryBar-report/reports-pages-filters/CmapaignsIfraTypes";
import { CAMPAIGNS_ALL_TIME_ID } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPicker.consts";
import {
    getTimeDiffBetweenDatesUTC,
    isAllTimeSelectedForCampaign,
} from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPickerUtils";
import { CampaignsDurationFilter } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignsDurationFilter";
import { CampaignsLocationFilter } from "app/components/campaigns/queryBar-report/reports-pages-filters/locationPicker/CampaignsLocationFilter";
import { getLanguagesForCampaigns } from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import { getUpdatedGranularityItemsState } from "app/components/campaigns/shared/overview/utils";
import {
    dashboardWizardCountryChanged,
    dashboardWizardCurrentCampaignChanged,
    dashboardWizardDurationChanged,
    dashboardWizardFilterChanged,
    dashboardWizardKeyAppend,
    dashboardWizardKeyRemove,
    dashboardWizardKeyReset,
    dashboardWizardMetricAndFamilyChanged,
    dashboardWizardWebSourceChanged,
} from "app/components/dashboard/widget-wizard/actions/dashboardWizardActionCreators";
import CampaignsDashboardWizardDevice from "app/pages/digital-marketing/keywords-campaigns/dashboard-widget-wizard/CampaignsDashboardWizardDevice";
import {
    CampaignsDurationFilterWrapper,
    CampaignsPickerWrapper,
    DashboardWizardMetricsWrapper,
    DeviceLocationsPickersWrapper,
    LocationsPickerWrapper,
    SectionTitle,
    SmallTitle,
} from "app/components/dashboard/widget-wizard/components/campaigns/CampaignsDashboardWizardStyled";
import type { ICampaignCompetitor } from "app/components/dashboard/widget-wizard/components/campaigns/CompetitorsDropdown";
import { CompetitorsDropdown } from "app/components/dashboard/widget-wizard/components/campaigns/CompetitorsDropdown";
import DashboardWizardMetrics from "app/components/dashboard/widget-wizard/components/DashboardWizardMetrics";
import { getSelectedLocationsForURL } from "app/components/widget/widget-utilities/Campaigns/CampaignsUtils";
import { getCampaignsConsumptionFromServer } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import {
    getAvailableDurationObjectForRTContext,
    getCampaignMaxAvailableDate,
    getDefaultDurationForRankTrackerCampaign,
    getRtCampaignDurationBounds,
    getCampaignsFromServer,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignData,
    IRTCurrentCampaignData,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { getScrapingConfsIdFromLocales } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/RankTrackerReportsSharedUtils/ReportsSharedUtils";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import DurationService from "app/services/DurationService";
import I18n from "pro-features/components/WithTranslation/src/I18n";

import DashboardWizardDynamicFilters from "app/components/dashboard/widget-wizard/components/DashboardWizardDynamicFilters";

import { TagsDropdown } from "app/pages/digital-marketing/keywords-campaigns/dashboard-widget-wizard/TagsDropdown";
import { allTrackers } from "app/services/track/track";

const initialKeyMetricList = [
    "RankTrackerOverviewVisibility",
    "RankTrackerOverviewWeightedAvgPosition",
    "RankTrackerOverviewClicks",
    "RankTrackerOverviewShareOfVoice",
];
const competitorFilterList = ["RankTrackerKeywordsReportTable"];

interface ICampaignsDashboardWizard {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    widgetPreview: any;
}

export const CampaignsDashboardWizard: FC<ICampaignsDashboardWizard> = ({ widgetPreview }) => {
    const customDashboard = useSelector((state) => state.customDashboard.dashboardWizard);
    const dispatch = useDispatch();
    const [languagesList, setLanguagesList] = useState<ISupportedLanguagesAndLocale[]>();
    const [campaignsConsumptionMetrics, setCampaignsConsumptionMetrics] = useState<ICampaignsConsumptionMetrics>({
        usedKeywords: 0,
        totalKeywords: swSettings.components.RankTracker.resources.NumberTrackedKeywords,
        usedCampaigns: 0,
        totalCampaigns: swSettings.components.RankTracker.resources.NumberOfCampaigns,
        hasMutualAssetsError: false,
    });
    const { hasMutualAssetsError } = campaignsConsumptionMetrics;
    useEffect(() => {
        (async () => {
            const languages = await getLanguagesForCampaigns(false);
            setLanguagesList(languages);
            await getCampaignsFromServer();
            const newStateCampaignConsumption = await getCampaignsConsumptionFromServer(campaignsConsumptionMetrics);
            setCampaignsConsumptionMetrics(newStateCampaignConsumption);
        })();
    }, []);

    const currentCampaignConfiguration: IRTCurrentCampaignData | null =
        customDashboard.widget.currentCampaignConfiguration;
    const campaignsConfigurations = customDashboard.allCampaignsConfigurations;
    const selectedLocationsIds =
        !customDashboard.widget.country || !currentCampaignConfiguration
            ? null
            : getSelectedLocationsForURL(
                  customDashboard.widget.country,
                  currentCampaignConfiguration?.scrapingConfigurations,
              );
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const availableDurationObject = getAvailableDurationObjectForRTContext(currentCampaignConfiguration);

    const changeDuration = (newDuration, comparedDuration) => {
        dispatch(dashboardWizardDurationChanged(customDashboard, newDuration, comparedDuration));
    };
    const changeLocationIds = (scarpingConfigs: string) => {
        dispatch(dashboardWizardCountryChanged(customDashboard, scarpingConfigs));
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "submit-ok",
            `Location/Apply Locations/${scarpingConfigs?.split(",").length.toString() || ""}`,
        );
    };
    const changeMetric = (metric) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `${metric || ""}`);
        dispatch(
            dashboardWizardMetricAndFamilyChanged(
                customDashboard,
                metric,
                customDashboard.widget.family,
                customDashboard.widget.key,
            ),
        );
        // resetting the keys when switching metrics because
        // we reset the current campaign  in the reducer
        dispatch(dashboardWizardKeyReset(customDashboard));
    };

    const changeCampaign = (selectedCampaign: IKeywordsCampaignData) => {
        let initialKey;
        if (initialKeyMetricList.includes(customDashboard.widget.metric)) {
            initialKey = [
                {
                    id: selectedCampaign.site,
                    image: selectedCampaign.asset,
                    name: selectedCampaign.site,
                },
                ...selectedCampaign.competitors.map((c) => ({
                    id: c.site,
                    name: c.site,
                    image: c.asset,
                })),
            ];
        }

        // Default widget duration from firstScrapeTime→lastScrapeDate; computed here (not in action creators) to satisfy app-shared boundary rules.
        dispatch(
            dashboardWizardCurrentCampaignChanged(
                customDashboard,
                selectedCampaign,
                initialKey,
                getDefaultDurationForRankTrackerCampaign(selectedCampaign),
            ),
        );
    };

    const keyAppend = (newKey) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Competitors/Select Competitor/${newKey.name || ""}`);
        dispatch(dashboardWizardKeyAppend(customDashboard, newKey));
    };
    const keyRemove = (newKey) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Competitors/Deselect Competitor/${newKey.name || ""}`);
        dispatch(dashboardWizardKeyRemove(customDashboard, newKey));
    };

    const changeWebSource = (newWebSource) => {
        allTrackers.dispatchTrackEvent("Switch", "switch", `Data Source/${newWebSource || ""}`);
        dispatch(dashboardWizardWebSourceChanged(customDashboard, newWebSource));
    };

    const handleSearchItemClick = (campaignId: string) => {
        const selectedCampaign = campaignsConfigurations?.find(
            (campaignConfig) => campaignConfig.id === campaignId,
        ) as IKeywordsCampaignData;
        if (!selectedCampaign) {
            return;
        }
        changeCampaign(selectedCampaign);
        setIsSearchOpen(false);
    };

    const onChangeLocationIds = (locationIds) => {
        if (!currentCampaignConfiguration) {
            return;
        }
        const selectedScrapingConfs = getScrapingConfsIdFromLocales(
            locationIds,
            currentCampaignConfiguration.scrapingConfigurations.filter((config) =>
                (customDashboard.widget.webSource as string).includes(config.device),
            ),
        );
        changeLocationIds(selectedScrapingConfs);
    };
    const selectedCompetitors =
        (currentCampaignConfiguration &&
            customDashboard.widget.key?.filter((key) => key.name !== currentCampaignConfiguration?.site)) ??
        [];

    const onChangeDynamicFilter = (filterChange) => {
        dispatch(dashboardWizardFilterChanged(customDashboard, filterChange));
    };

    const updateGranularityForCurrentDuration = (durationObject: DatesRange) => {
        const updatedGranularityStates = getUpdatedGranularityItemsState(durationObject);
        const isGranularityDisabled = updatedGranularityStates.find(
            (gr) => gr.value === customDashboard.widget.filters.timeGranularity,
        )?.disabled;
        if (isGranularityDisabled) {
            const firstAvailableGranularity = updatedGranularityStates.find((gr) => !gr.disabled).value;
            onChangeDynamicFilter([
                {
                    timeGranularity: firstAvailableGranularity,
                },
            ]);
        }
    };

    // Shared bounds for all-time detection and calendar display; replaces createdTime in isAllTimeSelectedForCampaign checks.
    const durationBounds = getRtCampaignDurationBounds(currentCampaignConfiguration);
    const campaignMaxDate = getCampaignMaxAvailableDate(currentCampaignConfiguration?.lastScrapeDate);

    const onChangeDuration = (duration: string) => {
        const durationObject: DatesRange = DurationService.getDurationObjectForCampaigns(duration, campaignMaxDate);
        const isAllTimeSelected = Boolean(
            durationBounds &&
            isAllTimeSelectedForCampaign(durationObject, durationBounds.dataStart, durationBounds.maxDate),
        );
        if (isAllTimeSelected) {
            changeDuration(CAMPAIGNS_ALL_TIME_ID, false);
        } else {
            changeDuration(duration, false);
        }
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Duration/${duration || ""}`);
        updateGranularityForCurrentDuration(durationObject);
    };

    const getSelectedDurationForCalendar = (): string => {
        const durationObject: DatesRange = DurationService.getDurationDataForCampaigns(
            customDashboard.widget.duration,
            durationBounds?.dataStart ?? dayjs.utc(campaignMaxDate).startOf("day"),
            campaignMaxDate,
        )?.raw;
        const isAllTimeSelected = Boolean(
            durationBounds &&
            isAllTimeSelectedForCampaign(durationObject, durationBounds.dataStart, durationBounds.maxDate),
        );
        const allTimeDays = isAllTimeSelected ? getTimeDiffBetweenDatesUTC(durationObject) : undefined;
        return allTimeDays !== undefined ? `${allTimeDays}d` : customDashboard.widget.duration;
    };

    const onCampaignsDropDownToggle = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Metric Type");
        }
    };
    const onCompetitorsDropDownToggle = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Competitors");
        }
    };
    const onTagsDropDownToggle = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Tags");
        }
    };
    const onLocationClicked = (location: ILocationObject, isRemoved: boolean) => {
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Location/${isRemoved ? "Deselect Location" : "Select Location"}/${location.text || ""}`,
        );
    };
    const onDurationWindowOpen = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "open", "Duration");
    };

    return (
        <>
            <DashboardWizardMetricsWrapper>
                <SectionTitle>
                    <I18n>home.dashboards.wizard.metricType.title</I18n>
                </SectionTitle>
                <DashboardWizardMetrics
                    changeMetric={changeMetric}
                    family={customDashboard.widget.family}
                    selectedMetric={customDashboard.widget.metric}
                    isSegmentsGroup={false}
                    selectedCountry={customDashboard.widget.country}
                    webSource={customDashboard.widget.webSource as string}
                    shouldTrackOnSelect={false}
                    onDropDownToggle={onCampaignsDropDownToggle}
                />
            </DashboardWizardMetricsWrapper>
            <SectionTitle>
                <I18n>home.dashboards.wizard.campaign.title</I18n>
            </SectionTitle>
            <CampaignsPickerWrapper>
                <CampaignPicker
                    campaignId={currentCampaignConfiguration?.id}
                    campaignsConfigurations={campaignsConfigurations ?? []}
                    handleBodyClick={() => setIsSearchOpen(false)}
                    handleSearchItemClick={handleSearchItemClick}
                    presentEditSettingBtn={false}
                    shouldOpenSearchBarIfNoCampaignSelected={false}
                    onSearchOpenStateChange={setIsSearchOpen}
                    isSearchOpen={isSearchOpen}
                    hasMutualAssetsError={hasMutualAssetsError}
                    hideSubtitle={true}
                    isLoading={false} //once navigating to dashboards the user is already having the campaigns in local storage
                />
            </CampaignsPickerWrapper>
            {competitorFilterList.includes(customDashboard.widget.metric) ? (
                <CompetitorsDropdown
                    currentCampaignConfiguration={currentCampaignConfiguration}
                    selectedSites={selectedCompetitors as ICampaignCompetitor[]}
                    keyAppend={keyAppend}
                    keyRemove={keyRemove}
                    onDropDownToggle={onCompetitorsDropDownToggle}
                />
            ) : null}
            <TagsDropdown
                campaignId={currentCampaignConfiguration?.id}
                widgetFilters={customDashboard.widget.filters}
                onApprove={onChangeDynamicFilter}
                onDropDownToggle={onTagsDropDownToggle}
            />
            <SectionTitle>
                <I18n>home.dashboards.wizard.settings.title</I18n>
            </SectionTitle>
            <DeviceLocationsPickersWrapper>
                <CampaignsDashboardWizardDevice
                    selectedTrafficSource={customDashboard.widget.webSource as string}
                    changeDevice={changeWebSource}
                    selectedCampaign={currentCampaignConfiguration}
                />
                <LocationsPickerWrapper>
                    <SmallTitle>
                        <I18n>home.dashboards.wizard.locationPicker.title</I18n>
                    </SmallTitle>
                    <CampaignsLocationFilter
                        currentCampaignConfiguration={currentCampaignConfiguration}
                        campaignsConfigurations={campaignsConfigurations ?? []}
                        languagesList={languagesList}
                        isLoading={!languagesList}
                        selectedLocationsIds={selectedLocationsIds}
                        device={customDashboard.widget.webSource as string}
                        isForDashboard={true}
                        pickerButtonHeight={38}
                        onChangeLocationIds={onChangeLocationIds}
                        onLocationSelected={onLocationClicked}
                    />
                </LocationsPickerWrapper>
            </DeviceLocationsPickersWrapper>

            <SmallTitle>
                <I18n>home.dashboards.wizard.duration.title</I18n>
            </SmallTitle>
            <CampaignsDurationFilterWrapper>
                <CampaignsDurationFilter
                    isDisabled={!currentCampaignConfiguration}
                    availableDurationObject={availableDurationObject}
                    placement={"top-right"}
                    onDurationChange={onChangeDuration}
                    duration={getSelectedDurationForCalendar()}
                    height={40}
                    onDurationWindowOpen={onDurationWindowOpen}
                />
            </CampaignsDurationFilterWrapper>
            <DashboardWizardDynamicFilters
                availableFilters={customDashboard.availableFilters}
                widgetFilters={customDashboard.widget.filters}
                widgetMetric={customDashboard.widget.metric}
                widgetWebSource={customDashboard.widget.webSource as string}
                widgetDuration={customDashboard.widget.duration}
                changeFilter={onChangeDynamicFilter}
                widgetType={customDashboard.widget.type}
                widgetPreview={widgetPreview?.data}
                widgetKeys={customDashboard.widget.key as []}
                widgetCountry={customDashboard.widget.country}
            />
        </>
    );
};
